import React, { useState, useEffect } from 'react';
import { collection, query, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../../firebaseconfig';
import { fetchStockCategories } from '../../utils/stockCategories';
import './Stock.css';

// Helper to fetch stock items
const fetchStockItems = async () => {
  try {
    const itemsRef = collection(db, 'stock_items');
    const q = query(itemsRef, orderBy('name', 'asc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error fetching stock items:', error);
    return [];
  }
};

// Helper to fetch expenses
const fetchExpenses = async () => {
  try {
    const expensesRef = collection(db, 'expenses');
    const q = query(expensesRef, orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      date: doc.data().date?.toDate?.() || new Date(doc.data().date),
      createdAt: doc.data().createdAt?.toDate?.() || new Date(doc.data().createdAt)
    }));
  } catch (error) {
    console.error('Error fetching expenses:', error);
    return [];
  }
};

const StockReport = () => {
  const [stockItems, setStockItems] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState('month');
  const [reportType, setReportType] = useState('balance');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN' }).format(amount || 0);
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [items, expensesData, cats] = await Promise.all([
          fetchStockItems(),
          fetchExpenses(),
          fetchStockCategories()
        ]);
        setStockItems(items);
        setExpenses(expensesData);
        setCategories(cats);
      } catch (e) {
        console.error('Error loading data:', e);
        setError('Failed to load report data');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const getCategoryName = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    return category ? category.name : 'Unknown';
  };

  const getStockBalanceReport = () => {
    return stockItems.map(item => ({
      ...item,
      categoryName: getCategoryName(item.categoryId),
      balance: item.currentBalance || 0
    })).sort((a, b) => {
      // Sort by negative balance first, then by name
      if (a.balance < 0 && b.balance >= 0) return -1;
      if (a.balance >= 0 && b.balance < 0) return 1;
      return a.name.localeCompare(b.name);
    });
  };

  const getStockInOutSummary = () => {
    const periodStart = getPeriodStart(selectedPeriod);
    const periodEnd = new Date();
    
    // Filter expenses by period and stock type
    const filteredExpenses = expenses.filter(expense => {
      const expenseDate = new Date(expense.date || expense.createdAt);
      return expenseDate >= periodStart && expenseDate <= periodEnd &&
             (expense.expenseType === 'stock_in' || expense.expenseType === 'stock_out');
    });

    // Group by stock item
    const itemSummary = {};
    
    filteredExpenses.forEach(expense => {
      if (!expense.stockItemId) return;
      
      if (!itemSummary[expense.stockItemId]) {
        const stockItem = stockItems.find(i => i.id === expense.stockItemId);
        itemSummary[expense.stockItemId] = {
          itemId: expense.stockItemId,
          itemName: stockItem?.name || 'Unknown Item',
          stockIn: 0,
          stockOut: 0,
          netChange: 0
        };
      }
      
      if (expense.expenseType === 'stock_in') {
        itemSummary[expense.stockItemId].stockIn += expense.amount || 0;
        itemSummary[expense.stockItemId].netChange += expense.amount || 0;
      } else if (expense.expenseType === 'stock_out') {
        itemSummary[expense.stockItemId].stockOut += expense.amount || 0;
        itemSummary[expense.stockItemId].netChange -= expense.amount || 0;
      }
    });

    return Object.values(itemSummary).sort((a, b) => b.stockIn - a.stockIn);
  };

  const getPeriodStart = (period) => {
    const now = new Date();
    const start = new Date(now);
    
    switch (period) {
      case 'today':
        start.setHours(0, 0, 0, 0);
        return start;
      case 'week':
        const day = start.getDay();
        const diffToMonday = (day === 0 ? -6 : 1) - day;
        start.setDate(start.getDate() + diffToMonday);
        start.setHours(0, 0, 0, 0);
        return start;
      case 'month':
        return new Date(start.getFullYear(), start.getMonth(), 1, 0, 0, 0, 0);
      case 'year':
        return new Date(start.getFullYear(), 0, 1, 0, 0, 0, 0);
      case 'lastMonth':
        return new Date(start.getFullYear(), start.getMonth() - 1, 1, 0, 0, 0, 0);
      case 'lastYear':
        return new Date(start.getFullYear() - 1, 0, 1, 0, 0, 0, 0);
      default:
        return new Date(0);
    }
  };

  const getPeriodLabel = (period) => {
    const labels = {
      today: 'Today',
      week: 'This Week',
      month: 'This Month',
      year: 'This Year',
      lastMonth: 'Last Month',
      lastYear: 'Last Year',
      all: 'All Time'
    };
    return labels[period] || period;
  };


  if (loading) {
    return <div className="loading">Loading report...</div>;
  }

  return (
    <div className="stock-report-container">
      <div className="stock-report-header">
        <h2>Stock Reports</h2>
        <div className="header-actions">
          <div className="filters">
            <div className="report-type-filter">
              <select
                value={reportType}
                onChange={(e) => setReportType(e.target.value)}
                className="category-select"
              >
                <option value="balance">Stock Balance</option>
                <option value="inOut">Stock In/Out Summary</option>
              </select>
            </div>
            {reportType === 'inOut' && (
              <div className="time-range-filter">
                <select
                  value={selectedPeriod}
                  onChange={(e) => setSelectedPeriod(e.target.value)}
                  className="category-select"
                >
                  <option value="today">Today</option>
                  <option value="week">This Week</option>
                  <option value="month">This Month</option>
                  <option value="lastMonth">Last Month</option>
                  <option value="year">This Year</option>
                  <option value="lastYear">Last Year</option>
                  <option value="all">All Time</option>
                </select>
              </div>
            )}
          </div>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {reportType === 'balance' && (
        <div className="report-section">
          <h3>Stock Balance Report</h3>
          <div className="transactions-table">
            <table>
              <thead>
                <tr>
                  <th>Item Name</th>
                  <th>Category</th>
                  <th>Current Balance</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {getStockBalanceReport().map(item => {
                  const isNegative = item.balance < 0;
                  return (
                    <tr key={item.id} className={isNegative ? 'low-stock-row' : ''}>
                      <td>{item.name}</td>
                      <td>{item.categoryName}</td>
                      <td>{formatCurrency(item.balance)}</td>
                      <td>
                        {isNegative ? (
                          <span className="low-stock-badge">Negative Balance</span>
                        ) : (
                          <span className="in-stock-badge">Positive Balance</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {reportType === 'inOut' && (
        <div className="report-section">
          <h3>Stock In/Out Summary ({getPeriodLabel(selectedPeriod)})</h3>
          {getStockInOutSummary().length === 0 ? (
            <div className="no-items">No stock-related expenses found for this period</div>
          ) : (
            <>
              <div className="transactions-table">
                <table>
                  <thead>
                    <tr>
                      <th>Item Name</th>
                      <th>Stock In</th>
                      <th>Stock Out</th>
                      <th>Net Change</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getStockInOutSummary().map((item, idx) => (
                      <tr key={idx}>
                        <td>{item.itemName}</td>
                        <td className="positive">{formatCurrency(item.stockIn)}</td>
                        <td className="negative">{formatCurrency(item.stockOut)}</td>
                        <td className={item.netChange >= 0 ? 'positive' : 'negative'}>
                          {formatCurrency(item.netChange)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="summary-cards" style={{ marginTop: '1.5rem' }}>
                <div className="summary-card">
                  <div className="summary-title">Total Stock In</div>
                  <div className="summary-value positive">
                    {formatCurrency(getStockInOutSummary().reduce((sum, item) => sum + item.stockIn, 0))}
                  </div>
                </div>
                <div className="summary-card">
                  <div className="summary-title">Total Stock Out</div>
                  <div className="summary-value negative">
                    {formatCurrency(getStockInOutSummary().reduce((sum, item) => sum + item.stockOut, 0))}
                  </div>
                </div>
                <div className="summary-card">
                  <div className="summary-title">Net Change</div>
                  <div className="summary-value">
                    {formatCurrency(getStockInOutSummary().reduce((sum, item) => sum + item.netChange, 0))}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default StockReport;

